import { useCallback, useState, useEffect } from 'react';
import useSWR from 'swr';
import get from 'lodash/get';
import { useSearchParams } from 'react-router';
import queryString from 'query-string';
import ProductList from '../../components/product-list';
import ProductListItem from '../../components/product-list-item';
import CategoryDetails from '../../components/category-details';
import config from '../../config';

function PageCategory({ lang }) {
  // Fetcher with retry logic (5 attempts with 1 second delay)
  const fetcher = useCallback(async (url) => {
    const maxRetries = 5;
    let lastError;

    for (let i = 0; i < maxRetries; i++) {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
      } catch (error) {
        lastError = error;
        console.error(`Fetch attempt ${i + 1} failed:`, error.message);
        if (i < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }
    throw lastError;
  }, []);

  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get('id');
  // const productCategoryId = searchParams.get('pid');

  // Use config for API endpoint
  const apiEndpoint = config.apiEndpoint;
  const isEnglish = lang === 'en';
  const fieldPostfix = isEnglish ? 'EN' : 'TC';

  // Get search params from URL
  const searchQuery = searchParams.get('ps') || '';
  const [searchValue, setSearchValue] = useState(searchQuery);
  const [activeSearch, setActiveSearch] = useState(searchQuery);

  useEffect(() => {
    setSearchValue(searchQuery);
    setActiveSearch(searchQuery);
  }, [searchQuery]);

  // Handle search submission
  const handleSearch = useCallback((value) => {
    setActiveSearch(value);
    if (!config.isWordPress) {
      const newParams = new URLSearchParams(window.location.search);
      if (value) {
        newParams.set('ps', value);
      } else {
        newParams.delete('ps');
      }
      const newUrl = `${window.location.pathname}?${newParams.toString()}`;
      window.history.pushState({}, '', newUrl);
    }
  }, []);

  let chosen = '';

  // Category data for breadcrumb
  const { data: category, error: categoryError, isLoading: categoryLoading } = useSWR(
    `${apiEndpoint}/api/categories?sort=ordering`,
    fetcher
  );
  const categories = get(category, 'data');
  // const { data: productCategory, error: productCategoryError } = useSWR(
  //   `${apiEndpoint}/api/product-categories?sort=ordering`,
  //   fetcher
  // );
  // const productCategories = get(productCategory, 'data');
  categories && categories.forEach(categoryList => {
    if (categoryList.documentId === categoryId) {
      chosen = isEnglish ? categoryList.nameEN : categoryList.nameTC;
    }
  });
  // productCategories && productCategories.forEach(productCategoryList => {
  //   if (productCategoryList.documentId === productCategoryId) {
  //     chosen = isEnglish ? productCategoryList.nameEN : productCategoryList.nameTC;
  //   }
  // });

  // Product data for category page with search filters
  const productParams = {
    fields: [
      `title${fieldPostfix}`,
      `school${fieldPostfix}`,
      'votes',
      'voteBatch',
    ],
    sort: 'ordering',
    populate: ['coverImage'],
    'pagination[limit]': 500,
  };

  if (categoryId) {
    productParams['filters[category][documentId]'] = categoryId;
  }
  // if (productCategoryId) {
  //   productParams['filters[productCategories][documentId]'] = productCategoryId;
  // }

  // Add search filters if search is active
  // Note: solution and teamMember are excluded from search per WordPress plugin logic
  if (activeSearch) {
    if (categoryId) {
      productParams['filters[$and][0][category][documentId]'] = categoryId;
    }
    // if (productCategoryId) {
    //   productParams['filters[$and][0][productCategories][documentId]'] = productCategoryId;
    // }

    productParams['filters[$and][1][$or][0][titleEN][$containsi]'] = activeSearch;
    productParams['filters[$and][1][$or][1][titleTC][$containsi]'] = activeSearch;
    productParams['filters[$and][1][$or][2][schoolEN][$containsi]'] = activeSearch;
    productParams['filters[$and][1][$or][3][schoolTC][$containsi]'] = activeSearch;
    productParams['filters[$and][1][$or][4][topicEN][$containsi]'] = activeSearch;
    productParams['filters[$and][1][$or][5][topicTC][$containsi]'] = activeSearch;
    productParams['filters[$and][1][$or][6][targetEN][$containsi]'] = activeSearch;
    productParams['filters[$and][1][$or][7][targetTC][$containsi]'] = activeSearch;
    productParams['filters[$and][1][$or][8][problemEN][$containsi]'] = activeSearch;
    productParams['filters[$and][1][$or][9][problemTC][$containsi]'] = activeSearch;
  }

  const { data: productData, error: productError, isLoading: productLoading } = useSWR(
    `${apiEndpoint}/api/products?${queryString.stringify(productParams)}`,
    fetcher
  );
  const productDataInfo = get(productData, 'data');

  // Handle loading state
  if (categoryLoading || productLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>{isEnglish ? 'Loading...' : '載入中...'}</p>
      </div>
    );
  }

  // Handle error state
  if (categoryError || productError) {
    return (
      <div style={{ padding: '20px' }}>
        <div className="error">
          <p>{isEnglish ? 'Error loading category. Please try again later.' : '載入類別時發生錯誤，請稍後再試。'}</p>
          {(categoryError?.message || productError?.message) && (
            <p>Error: {categoryError?.message || productError?.message}</p>
          )}
        </div>
      </div>
    );
  }

  // Handle no categories found
  if (!categories || categories.length === 0) {
    return (
      <div style={{ padding: '20px' }}>
        <p>{isEnglish ? 'No categories found.' : '沒有找到類別。'}</p>
      </div>
    );
  }

  return (
    <div>
      {
        categories && (
          <CategoryDetails
            chosen={chosen}
            categories={categories}
            // productCategories={productCategories}
            lang={lang}
          >
            <ProductList search onSearch={handleSearch} initialSearchValue={searchValue}>
              {productDataInfo && productDataInfo.length > 0 && (
                productDataInfo.map((item, index) => (
                  <ProductListItem
                    key={index}
                    product={item}
                    lang={lang}
                  />
                ))
              )}
            </ProductList>
            {!(productDataInfo && productDataInfo.length > 0) && (
              <p style={{ textAlign: 'center', padding: '20px' }}>
                {isEnglish ? 'No student teams found.' : '沒有找到學生隊伍。'}
              </p>
            )}
          </CategoryDetails>
        )
      }

    </div>
  );
}

export default PageCategory;
